<template>
  <div>
    <vs-tabs v-model="tabIndex">
      <vs-tab v-for="tab of tabList" :key="tab.name" :label="tab.label" @click="onClickTab(tab)" />
    </vs-tabs>

    <!-- search -->
    <search-nav @handleSearch="onClickSearch">
      <div class="search-layout w-full grid col-gap-4 flex-wrap xl:flex">
        <component
          v-for="(component, index) in stdSearchComponents"
          :key="index"
          v-if="showSearchNavItem(component['options'])"
          :is="component['component']"
          :label="component['label']"
          :query="component['query']"
          :options="component['options']"
          :onClickSearch="onClickSearch"
          v-model="nav.value[component['value']]"
        />
      </div>
    </search-nav>

    <!-- list -->
    <div>
      <fragment>
        <div class="w-full flex">
          <div class="flex mr-auto mb-4">
            <vs-select v-model="perPage" autocomplete class="mr-4" style="width: 110px">
              <vs-select-item v-for="v in [10, 20, 30, 50]" :key="v" :text="`${v}개씩 보기`" :value="v" />
            </vs-select>
          </div>
          <div>
            <vs-button type="border" color="primary" @click="downloadExcel">
              <i class="feather icon-file-text mr-3"></i>
              <span>엑셀 다운로드</span>
            </vs-button>
          </div>
        </div>
      </fragment>
      <div class="w-full overflow-x-auto">
        <div class="list">
          <div class="list-header list-grid">
            <div>No.</div>
            <div>사업장명</div>
            <div>업체명</div>
            <div>담당부서</div>
            <div>아이디</div>
            <div>내국인/외국인</div>
            <div>직책</div>
            <div>이름</div>
            <div>직종</div>
            <div>휴대폰번호</div>
            <div>이메일</div>
            <div>안전교육 만료일</div>
            <div>건강정보<br />(수축/이완)</div>
            <div>건강정보 유효기간</div>
          </div>
          <div class="list-body">
            <div v-for="(data, index) in paginationList" :key="index" class="list-grid cursor-pointer" @click="onClickItem(data)">
              <template>
                <div>{{ getItemNumber(index) }}</div>
                <div>{{ data.field_name || "-" }}</div>
                <div>{{ data.company ? data.company.com_name : "-" }}</div>
                <div>{{ data.section || "-" }}</div>
                <div>{{ data.username || "-" }}</div>
                <div>{{ data.foreign || "-" }}</div>
                <div>{{ data.rank || "-" }}</div>
                <div>{{ data.name || "-" }}</div>
                <div>{{ data.position || "-" }}</div>
                <div style="white-space: nowrap">{{ formatPhone(data.phone) || "-" }}</div>
                <div>{{ data.email || "-" }}</div>
                <div>{{ data.안전교육만료일 || "-" }}</div>
                <div>{{ data.수축이완 || "- / -" }}</div>
                <div>{{ data.유효기간 || "-" }}</div>
              </template>
            </div>
          </div>
        </div>
      </div>

      <div class="vs-con-table">
        <div class="con-pagination-table vs-table--pagination">
          <k-pagination :total="lastPage ? lastPage : 0" v-model="page"></k-pagination>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SearchNav from "@/components/nav/SearchNav.vue";
import SearchColumnKeyword from "@/views/list/components/SearchColumnKeyword.vue";
import FieldSelector from "@/components/selector/FieldSelector.vue";
import List from "@/views/boards/single-page/984/List.js";
import searchComponent from "@/views/list/components/include";
import KPagination from "@/components/kPagination/kPagination.vue";
import * as UIUtil from "@/util/UiUtil";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { applyEducationExpirationToUser } from "@/util/TrainingVideo";

// 엑셀 다운로드 시 한 번에 조회하는 회원 수 (한 번에 전체를 조회하면 서버 타임아웃(504)이 발생한다)
const EXCEL_CHUNK_SIZE = 300;
// 회원 목록 조회 실패 시 최대 시도 횟수
const EXCEL_MAX_RETRY = 3;
// 교육 수강 내역을 조회할 때 한 번에 조회하는 사용자 수
const EXCEL_TRAINING_BATCH_SIZE = 300;

export default {
  components: {
    KPagination,
    FieldSelector,
    SearchColumnKeyword,
    SearchNav,
  },
  props: {
    isPrint: Boolean,
    board: Object,
    header: Object,
    value: Object,
    mode: {
      type: String,
      default: "list",
    },
  },
  computed: {
    tabList() {
      const list = [{ code: 0, name: "page-regist-account-list", label: "회원관리" }];
      return list;
    },
    projectId() {
      return this.$route.params.proj_id;
    },

    stdSearchComponents() {
      const getComponents = List.components;
      if (getComponents) {
        // 중복제거 코드 : filter((value, index, self) => self.indexOf(value) === index)
        const stdSearchComponents = getComponents();
        if (stdSearchComponents && stdSearchComponents.length > 0) {
          for (const component of stdSearchComponents) {
            component.component = searchComponent[component["component"]];
          }
        }
        return stdSearchComponents;
      }
      return [];
    },
    paginationList() {
      return this.allList;
    },
    // 검색된 목록
    searchedList() {
      return this.allList;
    },
  },
  watch: {
    perPage() {
      // URL에서 상태를 로드하는 중이 아닐 때만 페이지를 1로 리셋
      if (!this.isLoadingFromQuery) {
        this.page = 1;
      }
      this.loadListBoardAll();
      this.updateQueryParams();
    },
    page() {
      this.loadListBoardAll();
      this.updateQueryParams();
    },
    "$route.query": {
      handler(newQuery) {
        this.loadStateFromQuery(newQuery);
      },
      immediate: true,
      deep: true,
    },
  },
  data() {
    return {
      tabIndex: 0,
      nav: {
        value: {},
      },
      page: 1,
      lastPage: 1,
      perPage: 10,
      total: 0,
      allList: [],
      isLoadingFromQuery: false,
    };
  },
  created() {
    this.initBreadcrumb();
    this.isLoadingFromQuery = true; // 초기 로드 시 플래그 설정
    this.loadStateFromQuery(this.$route.query);
    this.loadListBoardAll();
    this.updateQueryParams();
    this.$nextTick(() => {
      this.isLoadingFromQuery = false; // 초기 로드 완료 후 플래그 해제
    });
  },
  methods: {
    updateQueryParams() {
      const query = { ...this.$route.query };

      // 페이지 번호 설정
      query.page = this.page.toString();
      query.perPage = this.perPage.toString();

      // 검색 조건 설정
      if (this.nav.value && this.nav.value.column) {
        if (this.nav.value.column.head) {
          query.ck_t = this.nav.value.column.head;
        } else {
          delete query.ck_t;
        }

        if (this.nav.value.column.keyword) {
          query.ck_k = this.nav.value.column.keyword;
        } else {
          delete query.ck_k;
        }

        if (this.nav.value.field.fieldId) {
          query.f_id = this.nav.value.field.fieldId;
          query.f_name = this.nav.value.field.fieldName;
        } else {
          delete query.f_id;
          delete query.f_name;
        }
      } else {
        delete query.ck_t;
        delete query.ck_k;
        delete query.f_id;
        delete query.f_name;
      }

      // 현재 URL과 다른 경우에만 라우터 변경
      const currentQuery = JSON.stringify(this.$route.query);
      const newQuery = JSON.stringify(query);

      if (currentQuery !== newQuery) {
        this.$router.replace({ query }).catch((err) => {
          if (err.name !== "NavigationDuplicated") {
            throw err;
          }
        });
      }
    },

    loadStateFromQuery(query) {
      if (!query) return;

      this.isLoadingFromQuery = true; // URL에서 로드 중임을 표시

      // 페이지 번호와 페이지당 항목 수 복원
      if (query.page) {
        this.page = parseInt(query.page) || 1;
      }

      if (query.perPage) {
        this.perPage = parseInt(query.perPage) || 10;
      }

      // 검색 조건 복원
      if (query.ck_t || query.ck_k || query.f_id || query.f_name) {
        if (!this.nav.value.column) {
          this.nav.value.column = {};
        }

        if (query.ck_t) {
          this.nav.value.column.head = query.ck_t;
        }

        if (query.ck_k) {
          this.nav.value.column.keyword = query.ck_k;
        }

        if (query.f_id) {
          this.nav.value.field = {
            fieldId: query.f_id,
            fieldName: query.f_name,
          };
        } else {
          this.nav.value.field = {
            fieldId: "",
            fieldName: "",
          };
        }
      }

      // 다음 tick에서 플래그 해제 (모든 watcher 실행 완료 후)
      this.$nextTick(() => {
        this.isLoadingFromQuery = false;
      });
    },

    initBreadcrumb() {
      this.$store.commit("menu/UPDATE_PAGE_LIST", [{ title: "Home" }, { title: "작업자 관리" }, { title: "회원관리", active: true }]);
    },
    onClickTab(tab) {
      this.tabIndex = tab.code;
      this.clearStateFromLocalStorage();
      this.$router.push({
        name: tab.name,
        query: this.$route.query,
      });
    },
    showSearchNavItem(options) {
      if (options && options.visibleKeyValue) {
        for (const keyValue of options.visibleKeyValue) {
          const key = Object.keys(keyValue)[0];
          const value = keyValue[key];
          const navValue = this.findJsonDataWithDottedPath(this.nav.value, key);
          if (navValue !== value) {
            if (Array.isArray(navValue) && navValue.toString() === value) {
              return true;
            }
          } else {
            return true;
          }
        }
        return false;
      }
      return true;
    },
    async loadListBoardAll() {
      const conditions = List.getConditions(this.nav);
      const params = {
        page: this.page,
        perPage: this.perPage,
        projectId: this.projectId,
        orderTarget: "id", // 이름, 전화번호 등 암호화되어 있는 컬럼으로는 정렬할 수 없다
        orderDirection: "desc",
      };
      params.searchType = "and";
      if (conditions) {
        const metaConditions = [];
        for (const condition of conditions) {
          if (condition.key.includes("meta.")) {
            const metaKey = condition.key.split("meta.")[1];
            const metaOperator = condition.operator;
            const metaValue = condition.value;
            if (metaKey && metaValue) {
              metaConditions.push({ key: metaKey, operator: metaOperator, value: metaValue });
            }
          } else {
            params[condition.key] = condition.value;
          }
        }

        if (metaConditions.length > 0) {
          params.metaConditions = metaConditions;
        }
      }

      try {
        UIUtil.loading(true);
        const response = await this.$store.dispatch("member/LOAD_MEMBERS", params);
        if (response && response.data) {
          const allList = [];
          for (let i = 0; i < response.data.length; i++) {
            const data = response.data[i];

            const metas = data.metas;
            const metaKeys = {
              field_name: "field.field_name",
              section: "담당부서",
              foreign: "is_foreigner",
              안전교육만료일: "안전교육만료일",
              수축: "수축",
              이완: "이완",
              유효기간: "유효기간",
            };

            const metaValues = Object.keys(metaKeys).reduce((acc, key) => {
              const meta = metas.find((meta) => meta.meta_key === metaKeys[key]);
              acc[key] = meta ? meta.meta_value : "";
              return acc;
            }, {});

            const field_name = metaValues.field_name;
            const section = metaValues.section;
            const foreignValue = !!metaValues.foreign && metaValues.foreign === "1" ? "외국인" : "내국인";

            const 교육만료일자 = await applyEducationExpirationToUser(this.$store.dispatch, {
              projectId: this.projectId,
              userId: data.id,
            });

            const 기본교육만료일자 = 교육만료일자.기본교육만료일 || "";

            let 안전교육만료일 = "";
            if (metaValues.안전교육만료일) {
              안전교육만료일 = new Date(기본교육만료일자).getTime() > new Date(metaValues.안전교육만료일).getTime() ? 기본교육만료일자 : this.formatDate(metaValues.안전교육만료일);
            } else {
              안전교육만료일 = 기본교육만료일자;
            }

            allList.push({
              id: data.id,
              field_name,
              company: data.company, // 업체
              section, // 업체 부서
              username: data.username, // user id
              rank: data.rank, // 직책
              name: data.name, // 이름
              position: data.position, // 직종
              phone: data.phone, // 전화번호
              email: data.email, // 이메일
              foreign: foreignValue, // 외국인내국인
              안전교육만료일,
              수축이완: !metaValues.수축 && !metaValues.이완 ? "" : `${metaValues.수축}/${metaValues.이완}`,
              유효기간: metaValues.유효기간,
            });
          }
          this.allList = allList;
          this.lastPage = response.last_page;
          this.total = response.total; // total 값 설정
        } else {
          console.error("Response or response.data is undefined");
        }
      } catch (err) {
        console.error("Error fetching data", err);
      }
      UIUtil.loading(false);
    },

    onClickSearch() {
      this.page = 1;
      this.loadListBoardAll();
      this.updateQueryParams();
    },

    onClickItem(data) {
      this.$router.push({
        name: "page-regist-account-detail",
        params: { proj_id: this.projectId, id: data.id },
        query: this.$route.query, // 현재 쿼리 파라미터 유지
      });
    },

    formatDate(dateString) {
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },

    formatPhone(phone) {
      if (!phone) return "";

      // 하이픈 제거
      const cleanNum = phone.replace(/-/g, "");

      // 전화번호 길이에 따라 형식 적용
      if (cleanNum.length === 10) {
        // 10자리: 일반 전화번호 (02-XXXX-XXXX) 또는 휴대폰 (010-XXX-XXXX)
        if (cleanNum.startsWith("02")) {
          return cleanNum.replace(/(\d{2})(\d{4})(\d{4})/, "$1-$2-$3");
        } else {
          return cleanNum.replace(/(\d{3})(\d{3})(\d{4})/, "$1-$2-$3");
        }
      } else if (cleanNum.length === 11) {
        // 11자리: 휴대폰 (010-XXXX-XXXX)
        return cleanNum.replace(/(\d{3})(\d{4})(\d{4})/, "$1-$2-$3");
      } else if (cleanNum.length === 12) {
        // 12자리: 특수 번호
        return cleanNum.replace(/(\d{4})(\d{4})(\d{4})/, "$1-$2-$3");
      } else {
        // 다른 길이는 그대로 반환
        return cleanNum;
      }
    },

    // No. 값 계산 (id desc 정렬 기준으로 total에서 내림차순)
    getItemNumber(index) {
      return this.total - (this.page - 1) * this.perPage - index;
    },

    // 엑셀 다운로드용 목록 조회 파라미터를 생성한다
    buildExcelParams() {
      const conditions = List.getConditions(this.nav);
      const params = {
        page: 1,
        perPage: EXCEL_CHUNK_SIZE,
        projectId: this.projectId,
        orderTarget: "id",
        orderDirection: "desc",
      };
      params.searchType = "and";

      if (conditions) {
        const metaConditions = [];
        for (const condition of conditions) {
          if (condition.key.includes("meta.")) {
            const metaKey = condition.key.split("meta.")[1];
            const metaOperator = condition.operator;
            const metaValue = condition.value;
            if (metaKey && metaValue) {
              metaConditions.push({ key: metaKey, operator: metaOperator, value: metaValue });
            }
          } else {
            params[condition.key] = condition.value;
          }
        }

        if (metaConditions.length > 0) {
          params.metaConditions = metaConditions;
        }
      }

      return params;
    },

    // 전체 데이터를 청크 단위로 가져오는 메서드 (엑셀 다운로드용)
    // 한 번에 전체를 조회하면 회원 수가 많을 때 서버에서 504 가 발생하므로 페이지 단위로 나누어 조회한다
    async loadAllDataForExcel() {
      const params = this.buildExcelParams();

      // 1. 먼저 이번년도 강의목록을 한 번만 가져온다
      const trainingVideoListInfo = await this.$store.dispatch("board/LOAD_POST_LIST", {
        projectId: this.projectId,
        boardId: 1064,
        page: 1,
        perPage: 30,
      });
      const trainingVideoIds = trainingVideoListInfo.data.map((post) => post.id) || [];

      const allList = [];
      let total = 0;
      let lastPage = 1;
      let page = 1;

      UIUtil.loading(true, 0, 0, "명");

      // 2. 회원 목록을 청크 단위로 조회하면서 청크마다 교육 수강 내역을 함께 조회한다
      do {
        params.page = page;

        const response = await this.loadMembersWithRetry(params);
        if (!response || !response.data) break;

        total = response.total || 0;
        lastPage = response.last_page || 1;

        // 해당 청크에 속한 사용자의 교육 수강 내역만 조회한다
        const watchedTrainingVideos = await this.loadTrainingDataInBatches(response.data, trainingVideoIds);
        allList.push(...this.convertMembersForExcel(response.data, watchedTrainingVideos, trainingVideoIds));

        UIUtil.loading(true, allList.length, total, "명");
        page += 1;
      } while (page <= lastPage);

      return { data: allList, total };
    },

    // 회원 목록 조회 (일시적인 타임아웃(504) 등에 대비해 재시도한다)
    async loadMembersWithRetry(params) {
      let lastError = null;
      for (let attempt = 1; attempt <= EXCEL_MAX_RETRY; attempt++) {
        try {
          return await this.$store.dispatch("member/LOAD_MEMBERS", params);
        } catch (error) {
          lastError = error;
          console.error(`회원 목록 조회 실패 (page ${params.page}, 시도 ${attempt}/${EXCEL_MAX_RETRY})`, error);
        }
      }
      throw lastError;
    },

    // 조회된 회원 목록을 엑셀 출력용 데이터로 변환한다
    convertMembersForExcel(members, allWatchedTrainingVideos, trainingVideoIds) {
      const list = [];

      for (let i = 0; i < members.length; i++) {
        const data = members[i];

        const metas = data.metas;
        const metaKeys = {
          field_name: "field.field_name",
          section: "담당부서",
          foreign: "is_foreigner",
          안전교육만료일: "안전교육만료일",
          수축: "수축",
          이완: "이완",
          유효기간: "유효기간",
        };

        const metaValues = Object.keys(metaKeys).reduce((acc, key) => {
          const meta = metas.find((meta) => meta.meta_key === metaKeys[key]);
          acc[key] = meta ? meta.meta_value : "";
          return acc;
        }, {});

        const field_name = metaValues.field_name;
        const section = metaValues.section;
        const foreignValue = !!metaValues.foreign && metaValues.foreign === "1" ? "외국인" : "내국인";

        // 해당 사용자의 교육 수강 내역을 찾는다
        const userWatchedVideos = allWatchedTrainingVideos.filter((video) => video.user_id === data.id);

        // 수강내역 중 가장 최근에 수강한 교육이수일을 가져온다.
        let 교육이수일 = "";

        // trainingVideoIds 에 있는 교육을 모두 수강한 경우에만 교육이수일을 가져옵니다.
        const watchedTrainingVideosIds = [
          ...new Set(
            userWatchedVideos.map((post) => {
              const body = JSON.parse(post.post_content).body || {};
              return body.videoPostId;
            })
          ),
        ];

        if (trainingVideoIds.every((id) => watchedTrainingVideosIds.includes(id))) {
          // 모든 교육을 수강한 경우, 가장 최근의 교육이수일을 가져옵니다.
          교육이수일 = userWatchedVideos.reduce((acc, post) => {
            const body = JSON.parse(post.post_content).body || {};
            const 교육이수일 = body.교육이수일;
            if (교육이수일 && (!acc || new Date(acc) < new Date(교육이수일))) {
              return 교육이수일;
            }
            return acc;
          }, "");
        }

        let 교육만료일자 = "";
        if (교육이수일) {
          교육만료일자 = new Date(교육이수일).addDays(365).format("yyyy-MM-dd");
        }

        let 안전교육만료일 = "";
        if (metaValues.안전교육만료일) {
          안전교육만료일 = new Date(교육만료일자).getTime() > new Date(metaValues.안전교육만료일).getTime() ? 교육만료일자 : this.formatDate(metaValues.안전교육만료일);
        } else if (교육만료일자) {
          안전교육만료일 = 교육만료일자;
        }

        // 소속1/소속2 는 사용자의 section/department 컬럼, 발주부서는 virtual_company 이다
        const 소속1 = data.section || "";
        const 소속2 = data.department || "";
        const 발주부서 = data.virtual_company ? data.virtual_company.com_name || data.virtual_company.full_name || "" : "";

        list.push({
          id: data.id,
          field_name,
          company: data.company,
          section,
          소속1,
          소속2,
          발주부서,
          username: data.username,
          rank: data.rank,
          name: data.name,
          position: data.position,
          phone: data.phone,
          email: data.email,
          foreign: foreignValue,
          안전교육만료일,
          수축이완: !metaValues.수축 && !metaValues.이완 ? "" : `${metaValues.수축}/${metaValues.이완}`,
          유효기간: metaValues.유효기간,
        });
      }

      return list;
    },

    // 교육 데이터를 배치로 가져오는 메서드
    async loadTrainingDataInBatches(userData, trainingVideoIds) {
      const batchSize = EXCEL_TRAINING_BATCH_SIZE;
      const allWatchedTrainingVideos = [];

      // 사용자 ID 목록 추출
      const userIds = userData.map((user) => user.id);

      for (let i = 0; i < userIds.length; i += batchSize) {
        const batchUserIds = userIds.slice(i, i + batchSize);

        try {
          const batchWatchedVideos = await this.$store.dispatch("board/LOAD_POST_LIST", {
            projectId: this.projectId,
            boardId: 1065,
            page: 1,
            perPage: 99999, // 모든 데이터 가져오기
            conditions: [
              { key: "user_id", operator: "IN", value: batchUserIds },
              { key: "body.videoPostId", operator: "IN", value: trainingVideoIds },
              { key: "body.시험결과", operator: "=", value: "합격" },
            ],
          });

          if (batchWatchedVideos && batchWatchedVideos.data) {
            allWatchedTrainingVideos.push(...batchWatchedVideos.data);
          }
        } catch (error) {
          console.error(`교육 데이터 배치 로드 오류 (batch ${Math.floor(i / batchSize) + 1}):`, error);
        }
      }

      return allWatchedTrainingVideos;
    },

    // 엑셀 다운로드 메서드
    async downloadExcel() {
      try {
        UIUtil.loading(true, 0, 0, "명");

        // 전체 데이터 가져오기 (청크 단위로 조회하며 진행률이 표시된다)
        const result = await this.loadAllDataForExcel();
        const allData = result.data;
        const total = result.total;

        if (allData.length === 0) {
          this.$vs.notify({
            title: "알림",
            text: "다운로드할 데이터가 없습니다.",
            color: "warning",
          });
          return;
        }

        // 엑셀 파일 생성 단계로 전환 (진행률 표시 초기화)
        UIUtil.loading(true, 0, 0, "명");

        // ExcelJS 워크북 생성
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("회원관리");

        // 헤더 설정
        const headers = ["No.", "사업장명", "업체명", "발주부서", "소속1", "소속2", "담당부서", "아이디", "내국인/외국인", "직책", "이름", "직종", "휴대폰번호", "이메일", "안전교육 만료일", "건강정보(수축/이완)", "건강정보 유효기간"];

        // 헤더 행 추가
        const headerRow = worksheet.addRow(headers);
        headerRow.height = 25;

        // 헤더 스타일 적용
        headerRow.eachCell((cell, colNumber) => {
          cell.font = { bold: true, size: 11, name: "맑은 고딕" };
          cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFE0E0E0" },
          };
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
        });

        // 데이터 행 추가
        allData.forEach((item, index) => {
          const no = total - index; // No. 계산 (전체에서 내림차순)
          const row = worksheet.addRow([
            no,
            item.field_name || "-",
            item.company ? item.company.com_name : "-",
            item.발주부서 || "-",
            item.소속1 || "-",
            item.소속2 || "-",
            item.section || "-",
            item.username || "-",
            item.foreign || "-",
            item.rank || "-",
            item.name || "-",
            item.position || "-",
            this.formatPhone(item.phone) || "-",
            item.email || "-",
            item.안전교육만료일 || "-",
            item.수축이완 || "-",
            item.유효기간 || "-",
          ]);

          // 데이터 행 스타일 적용
          row.eachCell((cell, colNumber) => {
            cell.font = { size: 10, name: "맑은 고딕" };
            cell.alignment = { vertical: "middle", horizontal: "center" };
            cell.border = {
              top: { style: "thin" },
              left: { style: "thin" },
              bottom: { style: "thin" },
              right: { style: "thin" },
            };
          });
        });

        // 컬럼 너비 자동 조정
        worksheet.columns.forEach((column, index) => {
          const header = headers[index];
          if (header) {
            // 헤더 텍스트 길이 기반으로 최소 너비 설정
            const minWidth = Math.max(header.length + 2, 10);
            const maxWidth = 30;
            column.width = Math.min(minWidth, maxWidth);
          }
        });

        // 엑셀 파일 생성 및 다운로드
        const buffer = await workbook.xlsx.writeBuffer();
        const now = new Date();
        const fileName = `회원관리_${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, "0")}${String(now.getDate()).padStart(2, "0")}_${String(now.getHours()).padStart(2, "0")}${String(now.getMinutes()).padStart(2, "0")}.xlsx`;

        const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        saveAs(blob, fileName);

        this.$vs.notify({
          title: "성공",
          text: `엑셀 파일이 다운로드되었습니다. (${allData.length}건)`,
          color: "success",
        });
      } catch (error) {
        console.error("엑셀 다운로드 오류:", error);
        this.$vs.notify({
          title: "오류",
          text: "엑셀 다운로드 중 오류가 발생했습니다.",
          color: "danger",
        });
      } finally {
        UIUtil.loading(false);
      }
    },
  },
};
</script>
<style lang="scss" scoped>
.list {
  text-align: center;

  .list-header {
    font-weight: 700;
    & > div {
      background-color: #f5f5f5;
      border-top: 1px solid #e0e0e0;
      border-bottom: 1px solid #e0e0e0;
    }
  }
  .list-body {
    & > div > div {
      background-color: #ffffff;
      border-bottom: 1px solid #e0e0e0;
    }
    & > div:hover {
      background-color: #f5f5f5;
      color: #000;
      font-weight: bold;
    }
    & > div.active {
      background-color: #7e72f299;
      color: #fff;
    }
  }

  .list-grid {
    display: grid;
    grid-template-columns: 60px repeat(9, minmax(100px, 1fr)) 200px repeat(3, minmax(140px, 1fr));

    & > div {
      padding: 10px 4px;
      justify-content: center;
      align-content: center;
    }
  }
  .list-edit {
    display: none;
  }

  button {
    width: 32px;
    height: 32px;

    display: flex;
    align-items: center;
    justify-content: center;

    color: #7e72f2;
    background: none;
    border: 1px solid #7e72f2;
    border-radius: 4px;
    cursor: pointer;

    span {
      margin: 0;
    }
  }
}
</style>
